import pattern from 'patternomaly';
import { Theme } from '@mui/material/styles';

// Pattern types for better accessibility
// Using wave and diagonal patterns with high visibility
const PATTERN_TYPES = {
  diagonal: 'diagonal' as const,
  diagonal_right_left: 'diagonal-right-left' as const, 
  vertical: 'vertical' as const,
  horizontal: 'horizontal' as const,
  dot: 'dot' as const,
  dash: 'dash' as const,
  cross_dash: 'cross-dash' as const,
  zigzag: 'zigzag' as const,
  zigzag_vertical: 'zigzag-vertical' as const,
  weave: 'weave' as const,
  cross: 'cross' as const,
  disc: 'disc' as const,
  ring: 'ring' as const,
  square: 'square' as const,
  diamond: 'diamond' as const,
  triangle: 'triangle' as const
};

// Create patterns with colors and textures for accessibility
export const createAccessiblePattern = (
  color: string, 
  patternType: any = PATTERN_TYPES.diagonal,
  opacity: number = 0.4
): CanvasPattern | string => {
  try {
    // Create a pattern with the specified color and type
    const patternCanvas = pattern.draw(patternType, color, color, 10);
    
    if (patternCanvas) {
      return patternCanvas;
    }
    return color;
  } catch (error) {
    // Fallback to solid color if pattern generation fails
    return color;
  }
};

// Generate accessible dataset colors with patterns
export const generateAccessibleDatasets = (
  datasets: any[],
  theme: Theme,
  usePatterns: boolean = true
) => {
  const patterns = [
    PATTERN_TYPES.diagonal,
    PATTERN_TYPES.vertical,
    PATTERN_TYPES.dot,
    PATTERN_TYPES.horizontal,
    PATTERN_TYPES.cross,
    PATTERN_TYPES.dash,
    PATTERN_TYPES.zigzag,
    PATTERN_TYPES.weave
  ];

  return datasets.map((dataset, index) => {
    const baseColor = dataset.backgroundColor || theme.palette.primary.main;
    const borderColor = dataset.borderColor || baseColor;
    
    if (!usePatterns) {
      return dataset;
    }

    // Apply pattern to background
    const patternType = patterns[index % patterns.length];
    const patternColor = Array.isArray(baseColor) 
      ? baseColor.map((color: string) => createAccessiblePattern(color, patternType))
      : createAccessiblePattern(baseColor, patternType);

    return {
      ...dataset,
      backgroundColor: patternColor,
      borderColor: borderColor,
      borderWidth: 2, // Make borders more visible
      hoverBackgroundColor: Array.isArray(baseColor) 
        ? baseColor.map((color: string) => createAccessiblePattern(color, patternType, 0.6))
        : createAccessiblePattern(baseColor, patternType, 0.6),
      hoverBorderWidth: 3
    };
  });
};

// Function to get pattern for a specific color - using diagonal patterns only
export const getPatternForColor = (
  color: string,
  patternIndex: number = 0,
  forcePattern: boolean = false
): CanvasPattern | string => {
  // Always apply patterns to secondary bars (index 1), or if forced
  const shouldApplyPattern = forcePattern || patternIndex === 1;
  
  if (!shouldApplyPattern) {
    return color;
  }
  
  // Alternate between diagonal and diagonal-right-left for clarity
  const patterns = [
    PATTERN_TYPES.diagonal,              // Index 0: Primary color
    PATTERN_TYPES.diagonal_right_left,   // Index 1: Secondary color
    PATTERN_TYPES.diagonal,              // Index 2: Warning color (same as 0)
    PATTERN_TYPES.diagonal_right_left,   // Index 3: Additional (same as 1)
  ];
  
  const patternType = patterns[patternIndex % patterns.length];
  
  try {
    // patternomaly.draw(shape, backgroundColor, patternColor, size)
    // We want colored background with white pattern lines
    return pattern.draw(patternType, color, 'rgba(255, 255, 255, 0.5)', 10);
  } catch (error) {
    return color;
  }
};

// Create patterns for stacked bar charts with diagonal patterns only
export const createStackedPatterns = (colors: string[]): (CanvasPattern | string)[] => {
  const patterns = [
    PATTERN_TYPES.diagonal,              // First color: diagonal lines
    PATTERN_TYPES.diagonal_right_left,   // Second color: reverse diagonal
  ];
  
  return colors.map((color, index) => {
    try {
      // patternomaly.draw(shape, backgroundColor, patternColor, size)
      // Colored background with white pattern lines
      return pattern.draw(patterns[index % patterns.length], color, 'rgba(255, 255, 255, 0.5)', 10);
    } catch {
      return color;
    }
  });
};

// Helper to check if patterns should be enabled (based on user preference or system settings)
export const shouldUsePatterns = (): boolean => {
  // Check for user preference in localStorage
  const userPreference = localStorage.getItem('useAccessiblePatterns');
  if (userPreference !== null) {
    return userPreference === 'true';
  }
  
  // Default to false - patterns are opt-in via the toggle
  return false;
};

// Toggle pattern preference
export const togglePatternPreference = (enabled: boolean): void => {
  localStorage.setItem('useAccessiblePatterns', enabled.toString());
};